import { defineStore } from 'pinia';
import { setCssVar } from 'quasar';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import {
  PropertyConfigurationDTO,
  PropertyThemeDTO,
  HotelRatingDTO,
} from 'src/services/api/data-contracts';
import { Currency } from 'src/types';
import useUtils from 'src/composables/utils';
import { IImageData } from 'src/composables/utils';

const propertyApiService = Services.getService(Api);

export const usePropertyStore = defineStore('propertyStore', {
  state: () => ({
    propertyId: undefined as number | undefined,
    propertyCode: undefined as string | undefined,
    propertyName: undefined as string | undefined,
    propertyLogo: undefined as string | undefined,
    propertyLogoWidth: '100px' as string,
    propertyAddress: undefined as string | undefined,
    theme: undefined as PropertyThemeDTO | undefined,
    propertyImageUrl: undefined as string | undefined,

    googleTagManagerContainerId: null as string,
    facebookPixelId: null as string,

    config: undefined as PropertyConfigurationDTO | undefined,
    hotelRating: undefined as HotelRatingDTO | undefined,

    currency: 'GBP' as Currency,
    availableCurrencies: ['GBP'] as Array<Currency>,

    termsAndConditionsUrl: null as string,
    privacyPolicyUrl: null as string,

    creditCards: [] as string[],

    showContactUsDialog: false as boolean,
    websiteUrl: null as string,
    telephone: null as string,
    email: null as string,
    address: null as string,
    defaultPhoneCountryCode: 'GB' as string,

    payByPaystack: false as boolean,

    showRoomConfigurationQuestion: false as boolean,
    showRoomTypeOccupancy: false as boolean,

    enableChildrenSearch: true as boolean,
    enableInfantSearch: true as boolean,
    defaultOccupancyAdult: 1 as number,
    defaultOccupancyChildren: 0 as number,
    defaultOccupancyInfant: 0 as number,
    adultMaximumOccupancy: null as number | null,
    childMaximumOccupancy: null as number | null,
    maxNumberOfRoomsOnBooking: null as number,

    occupancyAdultLabelText: undefined as string | undefined,
    occupancyChildrenLabelText: undefined as string | undefined,
    occupancyInfantLabelText: undefined as string | undefined,
  }),
  getters: {
    getConfig(): PropertyConfigurationDTO | undefined {
      return this.config;
    },
  },
  actions: {
    async getProperty(propertyCodeOrId) {
      const propertyConfigData =
        await propertyApiService.propertyGet(propertyCodeOrId);

      this.setPropertyData(propertyConfigData.data);
    },
    setPropertyData(propertyConfigData: PropertyConfigurationDTO) {
      this.propertyId = propertyConfigData.PropertyId;
      this.propertyCode = propertyConfigData.PropertyCode;
      this.propertyName = propertyConfigData.PropertyName;
      this.propertyAddress = propertyConfigData.PropertyAddress;
      this.propertyLogo = propertyConfigData.LogoFileUrl;

      useUtils()
        .getImageDimensions(this.propertyLogo)
        .then((imageData: IImageData) => {
          if (imageData.ratio > 16 / 9) {
            this.propertyLogoWidth = '300px';
          }
        })
        .catch(() => {
          // no op if no logo file is found.
        });

      this.propertyImageUrl = propertyConfigData.PropertyImageUrl;

      this.hotelRating = propertyConfigData.HotelRating ?? {
        HasRating: false,
      };

      this.googleTagManagerContainerId =
        propertyConfigData.GoogleTagManagerContainerId;
      this.facebookPixelId = propertyConfigData.FacebookPixelId;

      this.currency = propertyConfigData.Currency;
      this.availableCurrencies =
        propertyConfigData.AvailableCurrencies as Array<Currency>;
      this.payByPaystack = propertyConfigData.PayByPaystack;

      this.privacyPolicyUrl = propertyConfigData.PrivacyPolicyUrl;
      this.termsAndConditionsUrl = propertyConfigData.TermsAndConditionsUrl;

      this.creditCards = propertyConfigData.CreditCards;

      this.websiteUrl = propertyConfigData.WebsiteUrl;
      this.email = propertyConfigData.Email;
      this.telephone = propertyConfigData.Telephone;
      this.address = propertyConfigData.Address;

      this.defaultPhoneCountryCode =
        propertyConfigData.DefaultPhoneCountryCode ?? 'GB';

      this.showRoomConfigurationQuestion =
        propertyConfigData.ShowRoomConfigurationQuestion;

      this.showRoomTypeOccupancy = propertyConfigData.ShowRoomTypeOccupancy;

      this.enableChildrenSearch = propertyConfigData.EnableChildrenSearch;
      this.enableInfantSearch = propertyConfigData.EnableInfantSearch;
      this.defaultOccupancyAdult = propertyConfigData.DefaultOccupancyAdult;
      this.defaultOccupancyChildren =
        propertyConfigData.DefaultOccupancyChildren;
      this.defaultOccupancyInfant = propertyConfigData.DefaultOccupancyInfant;
      this.maxNumberOfRoomsOnBooking =
        propertyConfigData.MaxNumberOfRoomsOnBooking;

      this.adultMaximumOccupancy = propertyConfigData.AdultMaximumOccupancy;
      this.childMaximumOccupancy = propertyConfigData.ChildMaximumOccupancy;

      this.occupancyAdultLabelText = propertyConfigData.OccupancyAdultLabelText;
      this.occupancyChildrenLabelText =
        propertyConfigData.OccupancyChildrenLabelText;
      this.occupancyInfantLabelText =
        propertyConfigData.OccupancyInfantLabelText;

      this.configureTheme(propertyConfigData.Theme);
    },
    configureTheme(theme: PropertyThemeDTO) {
      this.theme = theme;
      const utils = useUtils();

      if (this.theme) {
        setCssVar('primary', theme.Primary);
        setCssVar('secondary', theme.Secondary);
        setCssVar('accent', theme.Accent);
        setCssVar('tertiary', theme.Tertiary);
        setCssVar('dark', theme.Dark);
        setCssVar('dark-page', theme.DarkPage);
        setCssVar('positive', theme.Positive);
        setCssVar('negative', theme.Negative);
        setCssVar('info', theme.Info);
        setCssVar('warning', theme.Warning);

        setCssVar('light-page', theme.LightPage);
        setCssVar('brand-primary', theme.BrandPrimary);

        if (utils.isLightColor(theme.BrandPrimary)) {
          setCssVar('text-on-brand-primary', '#000000');
        } else if (!utils.isLightColor(theme.BrandPrimary)) {
          setCssVar('text-on-brand-primary', '#FFFFFF');
        }
      }
    },
  },
});
